import { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Chip,
  Avatar,
  useTheme,
  alpha,
  useMediaQuery,
  CircularProgress,
  Alert,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import ManageTeamPage from "./ManageTeamPage";
import ProfilePage from "./ProfilePage";
import { useProfile } from "../../hooks";
import { useTranslation } from "react-i18next";
import { getCompanyInfo } from "../../api/getCompanyInfo";
import BusinessPage from "./BusinessPage";
import ChangePasswordPage from "./ChangePasswordPage";
import PricingPage from "./PricingPage";
import LanguageSelector from "../../components/LanguageSelector";
import ThemeToggle from "../../components/ThemeToggle";
import JessToggleButton from "../../components/JessToggleButton";
import ManagePaymentPage from "./ManagePaymentPage";
import TacitsPage from "./TacitsPage";
import CreditsPage from "./CreditsPage";
import NotFoundPage from "../NotFound/NotFoundPage";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { isDevRole, isSuperAdminRole } from "../../utils/userUtils";
import logger from "../../utils/logger";
import { DashboardThemeProvider } from "../Dashboard/context/DashboardThemeContext";
import { useGlobalTheme } from "../../context/GlobalThemeContext";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";

export interface CompanyAddress {
  id: string;
  zip_code: string;
  street: string;
  number: string;
  city: string;
  state: string;
  country: string;
}

export interface CompanyInfo {
  id: string;
  name: string;
  fantasy_name: string;
  cnpj: string;
  area: string;
  telephone: string[];
  email: string;
  subscription: string;
  subscription_message: string;
  instagram_profile: string;
  website: string;
  address: CompanyAddress;
  admin_emails: string[];
  phone_numbers: string[];
  is_crm_connected: boolean;
  ai_session_id: string;
  crm: string;
}

const tabConfig = [
  { labelKey: "setting.profile", path: "profile" },
  {
    labelKey: "setting.business",
    path: "business",
    adminOnly: true,
    flagKey: "business" as const,
  },
  {
    labelKey: "setting.team",
    path: "team",
    flagKey: "team_management" as const,
  },
  {
    labelKey: "setting.subscription",
    path: "subscription",
    adminOnly: true,
    flagKey: "subscription" as const,
  },
  { labelKey: "setting.password", path: "password" },
  {
    labelKey: "setting.manage-payment",
    path: "manage-payment",
    disabled: true,
  },
  {
    labelKey: "setting.tacits",
    path: "tacits",
    requiresRole: true,
    flagKey: "tacits" as const,
  },
];

const pagesWithoutCompanyInfo = ["password", "profile", "tacits", "credits"];

// Permite acesso a /settings/credits apenas via navegação interna (botão na TacitsPage)
function CreditsGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  if (!location.state?.fromTacits) {
    navigate("/settings/tacits", { replace: true });
    return null;
  }
  return <CreditsPage />;
}

export default function SettingsLayout() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userProfile } = useProfile();
  const { isMobile: isMobileBreakpoint, isTablet } = useBreakpoint();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Show hamburger menu when screen is below 1024px (mobile or tablet)
  const shouldShowMobileMenu = isMobileBreakpoint || isTablet;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const { hasFlag } = useFeatureFlags();

  // Dark mode
  const isDark = useGlobalTheme();
  const toggleTheme = () => {
    const next = !isDark;
    localStorage.setItem("theme", next ? "dark" : "light");
    window.dispatchEvent(
      new CustomEvent("appThemeChange", { detail: { isDark: next } }),
    );
  };

  // Dark mode values
  const pageBg = isDark ? "#121212" : "#f6f6f6";
  const headerBg = isDark
    ? "rgba(30, 30, 30, 0.98)"
    : alpha(theme.palette.grey[200], 1);
  const contentBg = isDark ? "rgba(25, 25, 25, 0.98)" : "#f2f2f2";
  const iconColor = isDark ? "rgba(255,255,255,0.8)" : theme.palette.grey[900];
  const tabColor = isDark
    ? "rgba(255,255,255,0.7)"
    : alpha(theme.palette.grey[900], 0.9);

  function getUserRoleFromToken(): "ADMIN" | "EMPLOYEE" | undefined {
    const raw = localStorage.getItem("authUser");
    if (!raw) return undefined;

    try {
      const user = JSON.parse(raw) as { role?: string };
      return user.role as "ADMIN" | "EMPLOYEE" | undefined;
    } catch (error: unknown) {
      logger.error("invalid authUser in localStorage", error);
      return undefined;
    }
  }
  const userRole = getUserRoleFromToken();

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const currentTab = pathname.split("/")[2] || "profile";
  const needsCompanyInfo = !pagesWithoutCompanyInfo.includes(currentTab);

  useEffect(() => {
    if (!needsCompanyInfo) {
      setIsInitialLoad(false);
      return;
    }

    const fetchCompanyInfo = async () => {
      if (isInitialLoad && !companyInfo) {
        setLoading(true);
      }

      const timeout = setTimeout(() => {
        setError(t("Loading delayed - some information may be unavailable."));
        setCompanyInfo({
          id: "default",
          name: "Empresa",
          fantasy_name: "",
          cnpj: "",
          area: "",
          telephone: [],
          email: "",
          subscription: "",
          subscription_message: "",
          instagram_profile: "",
          website: "",
          address: {
            id: "",
            zip_code: "",
            street: "",
            number: "",
            city: "",
            state: "",
            country: "",
          },
          admin_emails: [],
          phone_numbers: [],
          is_crm_connected: false,
          ai_session_id: "",
          crm: "",
        });
        setLoading(false);
        setIsInitialLoad(false);
      }, 3000); // 3 seconds timeout

      try {
        setError(null);
        const info = await getCompanyInfo();
        clearTimeout(timeout);
        setCompanyInfo(info);
      } catch (err) {
        clearTimeout(timeout);
        setError(
          t("Error loading company information. Some features may be limited."),
        );
        setCompanyInfo({
          id: "default",
          name: "Empresa",
          fantasy_name: "",
          cnpj: "",
          area: "",
          telephone: [],
          email: "",
          subscription: "",
          subscription_message: "",
          instagram_profile: "",
          website: "",
          address: {
            id: "",
            zip_code: "",
            street: "",
            number: "",
            city: "",
            state: "",
            country: "",
          },
          admin_emails: [],
          phone_numbers: [],
          is_crm_connected: false,
          ai_session_id: "",
          crm: "",
        });
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchCompanyInfo();
  }, [currentTab, needsCompanyInfo]);

  if (loading && isInitialLoad && needsCompanyInfo) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: pageBg,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Box
            sx={{
              color: isDark ? "rgba(255,255,255,0.6)" : "text.secondary",
              fontSize: "0.875rem",
            }}
          >
            Loading company information...
          </Box>
        </Box>
      </Box>
    );
  }

  const primaryLabel = userProfile?.userName || "";
  const secondaryLabel = userProfile?.companyName || companyInfo?.name || "";
  const initial = primaryLabel.charAt(0).toUpperCase();

  const handleMenuClick = () => {
    // Dispatch custom event para abrir o sidebar
    window.dispatchEvent(
      new CustomEvent("sidebarToggle", {
        detail: { isOpen: true },
      }),
    );
  };

  const toggleSettingsMenu = () => {
    if (isSettingsMenuOpen) {
      // Start closing animation
      setIsClosing(true);
      // Close after animation completes (300ms)
      setTimeout(() => {
        setIsSettingsMenuOpen(false);
        setIsClosing(false);
      }, 300);
    } else {
      setIsSettingsMenuOpen(true);
      setIsClosing(false);
    }
  };

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
    window.dispatchEvent(new Event("languageChanged"));
    // Close menu with animation
    setIsClosing(true);
    setTimeout(() => {
      setIsSettingsMenuOpen(false);
      setIsClosing(false);
    }, 300);
  };

  return (
    <DashboardThemeProvider value={isDark}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          bgcolor: pageBg,
        }}
      >
        <Box
          sx={{
            bgcolor: headerBg,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            p: isMobile ? 1 : 4,
            position: "relative",
            // Add padding-right when settings button is visible to prevent overlap
            pr: shouldShowMobileMenu ? "56px" : undefined,
            borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
          }}
        >
          {/* Hamburger Menu Button - Mobile/Tablet (Esquerda) - Show when screen is below 1024px */}
          {shouldShowMobileMenu && (
            <IconButton
              onClick={handleMenuClick}
              sx={{
                color: iconColor,
                zIndex: 10,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
              aria-label="Menu"
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Settings Menu Button - Mobile/Tablet (Direita) - Show when screen is below 1024px */}
          {shouldShowMobileMenu && (
            <IconButton
              onClick={toggleSettingsMenu}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: isSettingsMenuOpen
                  ? "translateY(-50%) rotate(180deg)"
                  : "translateY(-50%) rotate(0deg)",
                transition: "transform 0.3s ease",
                color: iconColor,
                zIndex: 10,
                "@media (max-width: 737px)": {
                  top: "25%",
                },
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
              aria-label="Configurações"
            >
              <SettingsIcon />
            </IconButton>
          )}

          <Chip
            avatar={
              <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 1) }}>
                {initial}
              </Avatar>
            }
            label={`${primaryLabel} • ${secondaryLabel}`}
            sx={{
              backgroundImage: `linear-gradient(135deg,${theme.palette.tertiary.main}, ${theme.palette.primary.main})`,
              fontWeight: "bold",
              color: "white",
            }}
          />

          <Tabs
            value={currentTab}
            onChange={(_, v) => navigate(v)}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              flexGrow: 1,
              // Prevent tabs from overlapping the settings button on smaller screens
              marginRight: shouldShowMobileMenu ? "48px" : 0,
              overflow: "hidden",
              ".MuiTab-root": {
                textTransform: "none",
                minWidth: 100,
                color: tabColor,
                "&.Mui-selected": {
                  backgroundImage: `linear-gradient(90deg, ${theme.palette.tertiary.main}, ${theme.palette.primary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                },
              },
              ".MuiTabs-indicator": {
                height: 3,
                borderRadius: 3,
                backgroundImage: `linear-gradient(90deg, ${theme.palette.tertiary.main}, ${theme.palette.primary.main})`,
              },
            }}
          >
            {tabConfig
              .filter((tab) => {
                if (tab.disabled) return false;
                if (
                  tab.adminOnly &&
                  userRole !== "ADMIN" &&
                  !isSuperAdminRole()
                )
                  return false;
                if (
                  tab.requiresRole &&
                  tab.path === "tacits" &&
                  !isDevRole() &&
                  !isSuperAdminRole() &&
                  userRole !== "ADMIN"
                )
                  return false;
                if (tab.flagKey && !hasFlag(tab.flagKey)) return false;
                return true;
              })
              .map((tab) => (
                <Tab key={tab.path} label={t(tab.labelKey)} value={tab.path} />
              ))}
          </Tabs>

          {/* Jess Toggle + Theme Toggle + Language Selector - Desktop only */}
          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <JessToggleButton isDarkTheme={isDark} />
              <ThemeToggle isDarkTheme={isDark} onToggle={toggleTheme} />
              <LanguageSelector isDarkTheme={isDark} />
            </Box>
          )}
        </Box>

        {/* Settings Modal - Mobile/Tablet - Show when screen is below 1024px */}
        {shouldShowMobileMenu && isSettingsMenuOpen && (
          <>
            {/* Modal Content */}
            <Box
              sx={{
                position: "fixed",
                top: 60,
                right: 0,
                width: "280px",
                maxWidth: "90vw",
                bgcolor: isDark ? "rgba(30, 30, 30, 0.98)" : "#ffffff",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
                borderTop: "none",
                borderRadius: "0 0 0 16px",
                padding: "20px",
                zIndex: 1300,
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                animation: isClosing
                  ? "slideOutRight 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  : "slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "@keyframes slideInRight": {
                  from: { transform: "translateX(100%)", opacity: 0 },
                  to: { transform: "translateX(0)", opacity: 1 },
                },
                "@keyframes slideOutRight": {
                  from: { transform: "translateX(0)", opacity: 1 },
                  to: { transform: "translateX(100%)", opacity: 0 },
                },
                // Adjust top position for screens between 900px-1024px
                "@media (min-width: 900px) and (max-width: 1024px)": {
                  top: "112px",
                },
              }}
            >
              {/* Language Selector */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: isDark ? "rgba(255,255,255,0.9)" : "#1a202c",
                    mb: 1.5,
                    opacity: 0.9,
                  }}
                >
                  {t("settingsLayout.language")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {(
                    [
                      { code: "pt", flag: "🇧🇷" },
                      { code: "en", flag: "🇺🇸" },
                      { code: "es", flag: "🇪🇸" },
                    ] as const
                  ).map(({ code, flag }) => {
                    const isActive = i18n.language === code;
                    return (
                      <IconButton
                        key={code}
                        disableRipple
                        onClick={() => changeLanguage(code)}
                        sx={{
                          flex: 1,
                          height: 52,
                          borderRadius: "18px",
                          fontSize: "1.4rem",
                          border: isActive
                            ? "2px solid #7956F7"
                            : isDark
                              ? "1px solid rgba(255,255,255,0.15)"
                              : "2px solid rgba(121, 86, 247, 0.15)",
                          background: isActive
                            ? "linear-gradient(135deg, #7956F7 0%, #4C3699 100%)"
                            : isDark
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(255,255,255,0.95)",
                          boxShadow:
                            isActive || isDark
                              ? "none"
                              : "0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.1)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            background: isActive
                              ? "linear-gradient(135deg, #6b45e6 0%, #3b2688 100%)"
                              : isDark
                                ? "rgba(138, 79, 255, 0.25)"
                                : "rgba(121, 86, 247, 0.08)",
                            borderColor: isActive ? "#6b45e6" : "#7956F7",
                            transform: "translateY(-2px) scale(1.04)",
                          },
                        }}
                      >
                        {flag}
                      </IconButton>
                    );
                  })}
                </Box>
              </Box>

              {/* Theme Toggle */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: isDark ? "rgba(255,255,255,0.9)" : "#1a202c",
                    mb: 1.5,
                    opacity: 0.9,
                  }}
                >
                  {t("settingsLayout.theme")}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <ThemeToggle isDarkTheme={isDark} onToggle={toggleTheme} />
                </Box>
              </Box>

              {/* Jess AI Toggle */}
              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: isDark ? "rgba(255,255,255,0.9)" : "#1a202c",
                    mb: 1.5,
                    opacity: 0.9,
                  }}
                >
                  {t("ai_enabled") || "Jess AI"}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <JessToggleButton isDarkTheme={isDark} />
                </Box>
              </Box>
            </Box>
          </>
        )}

        <Box
          sx={{
            flexGrow: 1,
            overflowY:
              currentTab === "subscription" && !isMobile ? "hidden" : "auto",
            m: isMobile ? 1 : 2.5,
            p: isMobile ? 2 : 2.5,
            bgcolor: contentBg,
            borderRadius: "2.5rem",
            backdropFilter: "blur(5rem)",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.18)",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.3)",
            },
          }}
        >
          {error && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => window.location.reload()}
                >
                  {t("Reload")}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <Routes>
            <Route index element={<Navigate to="profile" replace />} />
            {userRole === "ADMIN" && companyInfo && hasFlag("business") && (
              <Route
                path="business"
                element={<BusinessPage companyInfo={companyInfo} />}
              />
            )}
            <Route path="profile" element={<ProfilePage />} />
            <Route
              path="team"
              element={
                companyInfo ? (
                  <ManageTeamPage companyInfo={companyInfo} />
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t("Loading team information...")}
                    </Typography>
                  </Box>
                )
              }
            />
            {userRole === "ADMIN" || isSuperAdminRole() ? (
              <Route
                path="subscription"
                element={<PricingPage companyInfo={companyInfo} />}
              />
            ) : (
              <Route
                path="subscription"
                element={<Navigate to="profile" replace />}
              />
            )}
            <Route path="password" element={<ChangePasswordPage />} />
            <Route path="manage-payment" element={<ManagePaymentPage />} />
            {(isDevRole() || isSuperAdminRole() || userRole === "ADMIN") && (
              <Route
                path="tacits"
                element={hasFlag("tacits") ? <TacitsPage /> : <NotFoundPage />}
              />
            )}
            {(isDevRole() || isSuperAdminRole() || userRole === "ADMIN") && (
              <Route
                path="credits"
                element={
                  hasFlag("tacits") && hasFlag("credits") ? (
                    <CreditsGuard />
                  ) : (
                    <NotFoundPage />
                  )
                }
              />
            )}
            <Route path="*" element={<Navigate to="profile" replace />} />
          </Routes>
        </Box>
      </Box>
    </DashboardThemeProvider>
  );
}
